import { DocumentAPI } from './API'
import { DocumentTypeDTO } from './dtos/document-type'
import DocumentType from './DocumentType'
import { collection } from 'infra/collection'
import { PrimeProps } from 'types/definitions'
import DataLoader from 'dataloader'

export type GetDocumentType = (
  id: string | undefined,
) => DocumentType | undefined

export type FetchDocumentTypes = () => Promise<DocumentType[]>

export type PrimeDocumentTypeByShortId = (
  shortId: string,
) => DocumentType | undefined

export type PrimeDocumentType = (
  props: PrimeProps<DocumentTypeDTO, 'id'>,
) => DocumentType | undefined

export default class DocumentTypeStore {
  types = collection<DocumentTypeDTO, DocumentType>({
    create: () => new DocumentType(),
  })

  constructor(private api: DocumentAPI) {}

  /**
   * Data loader
   */
  documentTypeLoader = new DataLoader(this._fetchManyTypes.bind(this))

  primeDocumentTypeByShortId: PrimeDocumentTypeByShortId = (
    short_id?: string,
  ): DocumentType | undefined => {
    if (!short_id) {
      return undefined
    }

    let model = this.types.find((x) => x.shortId === short_id)
    if (!model) {
      model = this.types.create({ short_id })
    }
    this.fetchDocumentType(short_id).then()

    return model
  }

  async fetchDocumentType(id: string): Promise<DocumentType | null> {
    const documentType = await this.documentTypeLoader.load(id)

    const existing = this.types.find((d) => d.shortId === id)
    if (existing) {
      return existing.set(documentType ?? DocumentType.none, {
        parse: true,
        stripUndefined: true,
      })
    }

    return this.types.set(documentType)
  }

  private async _fetchManyTypes(ids: readonly string[]) {
    return this.api.types.getMany({ ids }).then((dtos) => this.types.set(dtos))
  }

  async fetchTypes(): Promise<DocumentType[]> {
    const response = await this.api.types.find()
    return response.map((type) => {
      let existing = this.types.find((d) => d.shortId === type.short_id)
      if (existing) {
        return existing.set(type, { parse: true, stripUndefined: true })
      }
      existing = this.types.set(type)
      this.documentTypeLoader.prime(type.short_id, existing)
      return existing
    })
  }
}
